import type { ReactNode } from 'react';

export function Panel({
  title,
  subtitle,
  right,
  children,
  className = '',
  bodyClassName = '',
}: {
  title?: string;
  subtitle?: string;
  right?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={`panel ${className}`}>
      {(title || right) && (
        <header className="flex items-start justify-between gap-3 border-b border-edge px-4 py-3">
          <div>
            {title && <h2 className="text-[13px] font-semibold tracking-wide text-muted uppercase">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-dim">{subtitle}</p>}
          </div>
          {right}
        </header>
      )}
      <div className={bodyClassName || 'p-4'}>{children}</div>
    </section>
  );
}

export function Pill({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'up' | 'down' | 'burn' | 'brand';
  title?: string;
}) {
  const tones: Record<string, string> = {
    neutral: 'border-edge-bright text-muted',
    up: 'border-up/40 text-up bg-up/10',
    down: 'border-down/40 text-down bg-down/10',
    burn: 'border-burn/40 text-burn bg-burn/10',
    brand: 'border-brand/40 text-brand bg-brand/10',
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/** Horizontal meter. `value` is 0..1. */
export function Meter({ value, tone = 'up' }: { value: number; tone?: 'up' | 'down' | 'burn' | 'brand' }) {
  const pctWidth = Math.min(100, Math.max(0, value * 100));
  const bar: Record<string, string> = {
    up: 'bg-up',
    down: 'bg-down',
    burn: 'bg-burn',
    brand: 'bg-brand',
  };
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-edge">
      <div
        className={`h-full rounded-full transition-[width] duration-500 ${bar[tone]}`}
        style={{ width: `${pctWidth}%` }}
      />
    </div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-edge ${className}`} />;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-dim">{children}</p>;
}
